import {
  LoanCalculationInput,
  LoanCalculationResult,
  AmortizationScheduleItem,
  AmortizationAnnualSummary,
  AffordabilityCalculationResult,
  RefinanceCalculationResult,
  CalculationTraceStep,
  CompoundingConvention,
  LoanPaymentFrequency,
} from '../types';
import { LENDING_COUNTRY_PACKS, GOLDEN_LOAN_TEST_CASES } from '../data/loanCountryPacksData';

export function getPeriodsPerYear(frequency: LoanPaymentFrequency): number {
  switch (frequency) {
    case 'monthly':
      return 12;
    case 'semimonthly':
      return 24;
    case 'biweekly':
    case 'accelerated_biweekly':
      return 26;
    case 'weekly':
    case 'accelerated_weekly':
      return 52;
    case 'quarterly':
      return 4;
    case 'annual':
      return 1;
    default:
      return 12;
  }
}

/**
 * Converts a nominal annual interest rate to the exact periodic rate based on sovereign compounding rules.
 */
export function calculatePeriodicRate(
  nominalAnnualRatePercent: number,
  compounding: CompoundingConvention = 'MONTHLY',
  frequency: LoanPaymentFrequency = 'monthly'
): number {
  const nominalRate = nominalAnnualRatePercent / 100;
  const periodsPerYear = getPeriodsPerYear(frequency);

  if (nominalRate <= 0) return 0;

  switch (compounding) {
    case 'SEMI_ANNUAL': {
      // Canadian statutory rule (Interest Act): Semi-annual compounding
      // r_period = (1 + nominalRate / 2)^(2 / periodsPerYear) - 1
      return Math.pow(1 + nominalRate / 2, 2 / periodsPerYear) - 1;
    }
    case 'ANNUAL': {
      // European standard annual compounding
      // r_period = (1 + nominalRate)^(1 / periodsPerYear) - 1
      return Math.pow(1 + nominalRate, 1 / periodsPerYear) - 1;
    }
    case 'DAILY': {
      // Australian standard daily interest charging periodic rate
      // 365-day basis
      return (nominalRate / 365) * (365 / periodsPerYear);
    }
    case 'CONTINUOUS': {
      return Math.exp(nominalRate / periodsPerYear) - 1;
    }
    case 'MONTHLY':
    default: {
      // Standard US / UK nominal periodic rate: nominalRate / periodsPerYear
      return nominalRate / periodsPerYear;
    }
  }
}

/**
 * Standard closed-form amortizing payment formula
 */
export function calculateStandardPayment(
  principal: number,
  periodicRate: number,
  totalPeriods: number
): number {
  if (principal <= 0 || totalPeriods <= 0) return 0;
  if (periodicRate === 0) {
    return principal / totalPeriods;
  }

  const factor = Math.pow(1 + periodicRate, totalPeriods);
  const payment = principal * ((periodicRate * factor) / (factor - 1));
  return payment;
}

/**
 * Calculates Accelerated Bi-Weekly and Weekly payments
 * In Canada and Australia, Accelerated Bi-Weekly is strictly Monthly Payment / 2.
 * This results in 26 half-payments = 13 full monthly payments per year (1 extra month applied to principal).
 */
export function calculatePeriodicPaymentWithAcceleration(
  principal: number,
  nominalRatePercent: number,
  compounding: CompoundingConvention,
  frequency: LoanPaymentFrequency,
  amortizationYears: number
): number {
  const totalMonths = amortizationYears * 12;
  const monthlyPeriodicRate = calculatePeriodicRate(nominalRatePercent, compounding, 'monthly');
  const baseMonthlyPayment = calculateStandardPayment(principal, monthlyPeriodicRate, totalMonths);

  if (frequency === 'accelerated_biweekly') {
    return baseMonthlyPayment / 2;
  }

  if (frequency === 'accelerated_weekly') {
    return baseMonthlyPayment / 4;
  }

  // Standard regular frequency calculation
  const periodsPerYear = getPeriodsPerYear(frequency);
  const totalPeriods = amortizationYears * periodsPerYear;
  const periodicRate = calculatePeriodicRate(nominalRatePercent, compounding, frequency);
  return calculateStandardPayment(principal, periodicRate, totalPeriods);
}

/**
 * Generates the full amortization schedule with extra payments, penny drift correction, and annual rollups
 */
export function generateAmortizationSchedule(
  principal: number,
  periodicPayment: number,
  periodicRate: number,
  totalScheduledPeriods: number,
  startDate: Date = new Date(),
  frequency: LoanPaymentFrequency = 'monthly',
  extraPaymentMonthly: number = 0,
  extraPaymentAnnual: number = 0,
  oneTimeExtraAmount: number = 0,
  oneTimeExtraPaymentNumber: number = 0
): {
  schedule: AmortizationScheduleItem[];
  yearlySummaries: AmortizationAnnualSummary[];
  actualPaymentsCount: number;
  totalPrincipalPaid: number;
  totalInterestPaid: number;
  totalExtraPaid: number;
} {
  const schedule: AmortizationScheduleItem[] = [];
  const periodsPerYear = getPeriodsPerYear(frequency);

  let currentBalance = principal;
  let cumulativePrincipal = 0;
  let cumulativeInterest = 0;
  let cumulativeExtraPrincipal = 0;
  let paymentNum = 1;

  while (currentBalance > 0.005 && paymentNum <= totalScheduledPeriods * 2) {
    const openingBalance = currentBalance;
    const interestPortion = openingBalance * periodicRate;

    // Check extra payments applicable for this period
    let extraForThisPeriod = 0;
    if (extraPaymentMonthly > 0) {
      extraForThisPeriod += extraPaymentMonthly;
    }
    // Annual extra payment on the 12th / corresponding period of each year
    if (extraPaymentAnnual > 0 && paymentNum % periodsPerYear === 0) {
      extraForThisPeriod += extraPaymentAnnual;
    }
    // One-time extra injection
    if (oneTimeExtraAmount > 0 && paymentNum === oneTimeExtraPaymentNumber) {
      extraForThisPeriod += oneTimeExtraAmount;
    }

    let principalPortion = periodicPayment - interestPortion;
    let scheduledPmt = periodicPayment;

    // Check if remaining balance is less than standard principal portion (final payment adjustment)
    if (principalPortion + extraForThisPeriod >= openingBalance) {
      principalPortion = openingBalance;
      extraForThisPeriod = 0;
      scheduledPmt = principalPortion + interestPortion;
      currentBalance = 0;
    } else {
      currentBalance = openingBalance - (principalPortion + extraForThisPeriod);
      if (currentBalance < 0.005) {
        currentBalance = 0;
      }
    }

    cumulativePrincipal += principalPortion;
    cumulativeInterest += interestPortion;
    cumulativeExtraPrincipal += extraForThisPeriod;

    // Calculate approximate payment date
    const pDate = new Date(startDate.getTime());
    if (frequency === 'monthly') {
      pDate.setMonth(pDate.getMonth() + (paymentNum - 1));
    } else if (frequency === 'biweekly' || frequency === 'accelerated_biweekly') {
      pDate.setDate(pDate.getDate() + (paymentNum - 1) * 14);
    } else if (frequency === 'weekly' || frequency === 'accelerated_weekly') {
      pDate.setDate(pDate.getDate() + (paymentNum - 1) * 7);
    } else {
      pDate.setMonth(pDate.getMonth() + (paymentNum - 1) * (12 / periodsPerYear));
    }

    const dateString = pDate.toISOString().slice(0, 10);

    schedule.push({
      paymentNumber: paymentNum,
      paymentDate: dateString,
      openingBalance: Math.round(openingBalance * 100) / 100,
      scheduledPayment: Math.round(scheduledPmt * 100) / 100,
      principalPortion: Math.round(principalPortion * 100) / 100,
      interestPortion: Math.round(interestPortion * 100) / 100,
      extraPrincipal: Math.round(extraForThisPeriod * 100) / 100,
      totalPayment: Math.round((scheduledPmt + extraForThisPeriod) * 100) / 100,
      closingBalance: Math.round(currentBalance * 100) / 100,
      cumulativePrincipal: Math.round(cumulativePrincipal * 100) / 100,
      cumulativeInterest: Math.round(cumulativeInterest * 100) / 100,
      cumulativeExtraPrincipal: Math.round(cumulativeExtraPrincipal * 100) / 100,
    });

    if (currentBalance === 0) break;
    paymentNum++;
  }

  // Generate Year-by-Year Rollup
  const yearlySummaries: AmortizationAnnualSummary[] = [];
  let currentYear = 1;
  let yearOpeningBalance = principal;
  let yearPrincipal = 0;
  let yearInterest = 0;
  let yearExtra = 0;
  let yearTotal = 0;

  for (let i = 0; i < schedule.length; i++) {
    const item = schedule[i];
    yearPrincipal += item.principalPortion;
    yearInterest += item.interestPortion;
    yearExtra += item.extraPrincipal;
    yearTotal += item.totalPayment;

    const isYearEnd = (i + 1) % periodsPerYear === 0 || i === schedule.length - 1;
    if (isYearEnd) {
      yearlySummaries.push({
        yearNumber: currentYear,
        openingBalance: Math.round(yearOpeningBalance * 100) / 100,
        principalPaid: Math.round(yearPrincipal * 100) / 100,
        interestPaid: Math.round(yearInterest * 100) / 100,
        extraPrincipalPaid: Math.round(yearExtra * 100) / 100,
        totalPaid: Math.round(yearTotal * 100) / 100,
        closingBalance: item.closingBalance,
      });

      currentYear++;
      yearOpeningBalance = item.closingBalance;
      yearPrincipal = 0;
      yearInterest = 0;
      yearExtra = 0;
      yearTotal = 0;
    }
  }

  return {
    schedule,
    yearlySummaries,
    actualPaymentsCount: schedule.length,
    totalPrincipalPaid: Math.round(cumulativePrincipal * 100) / 100,
    totalInterestPaid: Math.round(cumulativeInterest * 100) / 100,
    totalExtraPaid: Math.round(cumulativeExtraPrincipal * 100) / 100,
  };
}

/**
 * Newton-Raphson Internal Rate of Return (IRR) Solver for APR / APRC
 * NetDisbursed = LoanPrincipal - UpfrontFinancedFees
 * Solves for r where NetDisbursed = sum( Payment_t / (1 + r)^t )
 */
export function calculateAprWithFees(
  netDisbursed: number,
  periodicPayment: number,
  numberOfPeriods: number,
  frequency: LoanPaymentFrequency = 'monthly'
): number {
  if (netDisbursed <= 0 || periodicPayment <= 0 || numberOfPeriods <= 0) return 0;

  const periodsPerYear = getPeriodsPerYear(frequency);
  let r = 0.05 / periodsPerYear; // initial guess: 5% annual

  const maxIterations = 60;
  const tolerance = 1e-7;

  for (let i = 0; i < maxIterations; i++) {
    // f(r) = sum( payment / (1+r)^t ) - netDisbursed
    // In annuity closed form: f(r) = payment * (1 - (1+r)^-n) / r - netDisbursed
    if (r === 0) r = 0.0001;

    const term = Math.pow(1 + r, -numberOfPeriods);
    const f = periodicPayment * ((1 - term) / r) - netDisbursed;

    // Derivative f'(r):
    // d/dr [ payment * (1 - (1+r)^-n) / r ] = payment * [ n*r*(1+r)^(-n-1) - (1 - (1+r)^-n) ] / r^2
    const df =
      periodicPayment *
      ((numberOfPeriods * r * Math.pow(1 + r, -numberOfPeriods - 1) - (1 - term)) /
        (r * r));

    if (Math.abs(df) < 1e-12) break;

    const nextR = r - f / df;

    if (Math.abs(nextR - r) < tolerance) {
      r = nextR;
      break;
    }
    r = nextR;
    if (r < 0) r = 0.00001;
  }

  const annualizedApr = r * periodsPerYear * 100;
  return Math.round(annualizedApr * 1000) / 1000;
}

/**
 * Master Main Loan & Mortgage Calculator Function
 */
export function calculateLoan(input: LoanCalculationInput): LoanCalculationResult {
  const trace: CalculationTraceStep[] = [];
  const countryCode = input.countryCode || 'US';
  const pack = LENDING_COUNTRY_PACKS[countryCode] || LENDING_COUNTRY_PACKS['US'];

  const currency = input.currency || pack.defaultCurrency;
  const compounding = input.compoundingConvention || pack.standardCompounding;
  const frequency = input.paymentFrequency || 'monthly';
  const periodsPerYear = getPeriodsPerYear(frequency);

  // Property & Principal Derivation
  let purchasePrice = input.purchasePrice || input.loanAmount;
  let downPayment = input.downPayment || 0;
  let downPaymentPercent = input.downPaymentPercent || 0;

  if (input.productType === 'MORTGAGE' && input.purchasePrice) {
    if (input.downPaymentPercent && input.downPaymentPercent > 0) {
      downPayment = (purchasePrice * input.downPaymentPercent) / 100;
    } else if (downPayment > 0) {
      downPaymentPercent = (downPayment / purchasePrice) * 100;
    }
  }

  let loanPrincipal = input.purchasePrice ? purchasePrice - downPayment : input.loanAmount;

  // Add financed fees if any
  const totalFees = input.fees?.reduce((acc, f) => acc + f.amount, 0) || 0;
  const financedFees = input.fees?.filter((f) => f.isFinanced).reduce((acc, f) => acc + f.amount, 0) || 0;
  const upfrontFees = (input.fees?.filter((f) => !f.isFinanced).reduce((acc, f) => acc + f.amount, 0) || 0) + (input.closingCostsUpfront || 0);

  loanPrincipal += financedFees;
  if (loanPrincipal <= 0) loanPrincipal = 10000;

  const ltv = purchasePrice > 0 ? (loanPrincipal / purchasePrice) * 100 : 100;

  trace.push({
    stepNumber: 1,
    name: 'Principal Derivation',
    category: 'GROSS',
    description: `Determined loan principal: Purchase Price ${purchasePrice.toLocaleString()} - Down Payment ${downPayment.toLocaleString()} + Financed Fees ${financedFees.toLocaleString()} = ${loanPrincipal.toLocaleString()} ${currency} (LTV: ${ltv.toFixed(1)}%)`,
    amountBefore: purchasePrice,
    adjustment: -downPayment + financedFees,
    amountAfter: loanPrincipal,
    ruleReference: 'Standard Principal Equation',
    formula: 'Purchase Price - Down Payment + Financed Fees',
  });

  // Terms & Periodic Rate
  const amortizationYears = input.amortizationYears || input.termYears || pack.standardMortgageAmortizationYears;
  const totalScheduledPeriods = amortizationYears * periodsPerYear;
  const periodicRate = calculatePeriodicRate(input.interestRate, compounding, frequency);

  trace.push({
    stepNumber: 2,
    name: 'Rate Normalization',
    category: 'PRE_TAX',
    description: `Converted nominal annual rate ${input.interestRate}% to periodic rate ${(periodicRate * 100).toFixed(6)}% using ${compounding} compounding and ${frequency} frequency (${periodsPerYear} periods/yr).`,
    amountBefore: input.interestRate,
    adjustment: 0,
    amountAfter: periodicRate * 100,
    ruleReference: 'Sovereign Compounding Formula',
    formula: 'Periodic Rate Conversion Formula',
  });

  // Base Payment Calculation
  const periodicPayment = calculatePeriodicPaymentWithAcceleration(
    loanPrincipal,
    input.interestRate,
    compounding,
    frequency,
    amortizationYears
  );

  // Pure Monthly Principal & Interest Equivalent
  const monthlyPeriodicRate = calculatePeriodicRate(input.interestRate, compounding, 'monthly');
  const pureMonthlyPayment = calculateStandardPayment(loanPrincipal, monthlyPeriodicRate, amortizationYears * 12);

  trace.push({
    stepNumber: 3,
    name: 'Payment Formula',
    category: 'NET',
    description: `Calculated periodic payment: ${periodicPayment.toFixed(2)} ${currency} (${frequency}). Monthly Principal & Interest: ${pureMonthlyPayment.toFixed(2)} ${currency}.`,
    amountBefore: loanPrincipal,
    adjustment: 0,
    amountAfter: periodicPayment,
    ruleReference: 'Amortization Annuity Equation',
    formula: 'P * (r * (1 + r)^n) / ((1 + r)^n - 1)',
  });

  // Escrow / Housing Costs (Property Tax, Home Insurance, PMI, HOA)
  let propertyTaxMonthly = 0;
  if (input.propertyTaxAnnual !== undefined && input.propertyTaxAnnual > 0) {
    propertyTaxMonthly = input.propertyTaxAnnual / 12;
  } else if (input.productType === 'MORTGAGE' && purchasePrice > 0) {
    propertyTaxMonthly = (purchasePrice * pack.defaultPropertyTaxRate) / 12;
  }

  let homeInsuranceMonthly = 0;
  if (input.homeInsuranceAnnual !== undefined && input.homeInsuranceAnnual > 0) {
    homeInsuranceMonthly = input.homeInsuranceAnnual / 12;
  } else if (input.productType === 'MORTGAGE' && purchasePrice > 0) {
    homeInsuranceMonthly = (purchasePrice * pack.defaultHomeInsuranceRate) / 12;
  }

  let mortgageInsuranceMonthly = input.mortgageInsuranceMonthly || 0;
  if (!mortgageInsuranceMonthly && input.productType === 'MORTGAGE') {
    if (ltv > pack.mortgageInsuranceThresholdLtv) {
      // Estimated ~0.55% annual PMI for US or standard tiered insurance
      mortgageInsuranceMonthly = (loanPrincipal * 0.0055) / 12;
    }
  }

  const hoaMonthly = input.hoaMonthly || 0;
  const estimatedMonthlyHousingPayment =
    pureMonthlyPayment + propertyTaxMonthly + homeInsuranceMonthly + mortgageInsuranceMonthly + hoaMonthly;

  trace.push({
    stepNumber: 4,
    name: 'Housing Escrow Breakdown',
    category: 'POST_TAX',
    description: `Computed monthly housing components: P&I ${pureMonthlyPayment.toFixed(2)} + Tax ${propertyTaxMonthly.toFixed(2)} + Insurance ${homeInsuranceMonthly.toFixed(2)} + Mortgage Insurance ${mortgageInsuranceMonthly.toFixed(2)} + HOA ${hoaMonthly.toFixed(2)} = Total Housing Payment (PITI) ${estimatedMonthlyHousingPayment.toFixed(2)} ${currency}.`,
    amountBefore: pureMonthlyPayment,
    adjustment: propertyTaxMonthly + homeInsuranceMonthly + mortgageInsuranceMonthly + hoaMonthly,
    amountAfter: estimatedMonthlyHousingPayment,
    ruleReference: 'PITI Escrow Equation',
    formula: 'Principal & Interest + Taxes + Insurance + PMI + HOA',
  });

  // Generate Full Amortization Schedule (incorporating extra payments if provided)
  const extraMonthly = input.extraPaymentMonthly || 0;
  const extraAnnual = input.extraPaymentAnnual || 0;
  const oneTimeAmount = input.oneTimeExtraAmount || 0;
  const oneTimePmtNum = input.oneTimeExtraPaymentNumber || 0;

  const {
    schedule,
    yearlySummaries,
    actualPaymentsCount,
    totalPrincipalPaid,
    totalInterestPaid,
    totalExtraPaid,
  } = generateAmortizationSchedule(
    loanPrincipal,
    periodicPayment,
    periodicRate,
    totalScheduledPeriods,
    new Date(),
    frequency,
    extraMonthly,
    extraAnnual,
    oneTimeAmount,
    oneTimePmtNum
  );

  // Baseline schedule without extra payments to compute exact interest saved
  let interestSaved = 0;
  let monthsSaved = 0;
  if (extraMonthly > 0 || extraAnnual > 0 || oneTimeAmount > 0 || frequency === 'accelerated_biweekly' || frequency === 'accelerated_weekly') {
    const baseline = generateAmortizationSchedule(
      loanPrincipal,
      pureMonthlyPayment,
      monthlyPeriodicRate,
      amortizationYears * 12,
      new Date(),
      'monthly',
      0,
      0,
      0,
      0
    );
    interestSaved = Math.max(0, baseline.totalInterestPaid - totalInterestPaid);
    const baselineMonths = baseline.actualPaymentsCount;
    const actualMonthsEquivalent = Math.round((actualPaymentsCount / periodsPerYear) * 12);
    monthsSaved = Math.max(0, baselineMonths - actualMonthsEquivalent);
  }

  // Calculate APR / APRC via IRR solver
  const netDisbursed = loanPrincipal - upfrontFees;
  const calculatedApr = calculateAprWithFees(netDisbursed, periodicPayment, totalScheduledPeriods, frequency);

  // Payoff dates
  const startDate = new Date();
  const scheduledPayoffDate = new Date(startDate.getTime());
  scheduledPayoffDate.setMonth(scheduledPayoffDate.getMonth() + amortizationYears * 12);

  const actualPayoffDate = new Date(startDate.getTime());
  const actualYears = actualPaymentsCount / periodsPerYear;
  actualPayoffDate.setMonth(actualPayoffDate.getMonth() + Math.round(actualYears * 12));

  const totalRepayment = totalPrincipalPaid + totalInterestPaid + totalExtraPaid;
  const totalHousingCostPaid =
    totalRepayment + (propertyTaxMonthly + homeInsuranceMonthly + mortgageInsuranceMonthly + hoaMonthly) * actualPaymentsCount * (12 / periodsPerYear);

  return {
    calculationId: `LOAN-CALC-${Date.now()}`,
    calculatedAt: new Date().toISOString(),
    input,
    countryName: pack.countryName,
    currency,
    principalAndInterestPayment: Math.round(pureMonthlyPayment * 100) / 100,
    periodicPayment: Math.round(periodicPayment * 100) / 100,
    estimatedMonthlyHousingPayment: Math.round(estimatedMonthlyHousingPayment * 100) / 100,
    totalPrincipalPaid: Math.round(totalPrincipalPaid * 100) / 100,
    totalInterestPaid: Math.round(totalInterestPaid * 100) / 100,
    totalExtraPaid: Math.round(totalExtraPaid * 100) / 100,
    totalFeesPaid: Math.round((totalFees + upfrontFees) * 100) / 100,
    totalRepayment: Math.round(totalRepayment * 100) / 100,
    totalHousingCostPaid: Math.round(totalHousingCostPaid * 100) / 100,
    totalScheduledPayments: totalScheduledPeriods,
    actualPaymentsToPayoff: actualPaymentsCount,
    payoffDateScheduled: scheduledPayoffDate.toISOString().slice(0, 10),
    payoffDateActual: actualPayoffDate.toISOString().slice(0, 10),
    monthsSaved,
    interestSaved: Math.round(interestSaved * 100) / 100,
    nominalRate: input.interestRate,
    effectiveRate: Math.round((Math.pow(1 + periodicRate, periodsPerYear) - 1) * 10000) / 100,
    calculatedAprOrAprc: calculatedApr,
    aprMetricLabel: pack.aprMetricName,
    aprDisclosureNote: pack.assumptions[1] || 'Includes interest and amortized mandatory finance charges.',
    purchasePrice: Math.round(purchasePrice * 100) / 100,
    downPaymentAmount: Math.round(downPayment * 100) / 100,
    downPaymentPercent: Math.round(downPaymentPercent * 10) / 10,
    loanToValueRatio: Math.round(ltv * 10) / 10,
    propertyTaxMonthly: Math.round(propertyTaxMonthly * 100) / 100,
    homeInsuranceMonthly: Math.round(homeInsuranceMonthly * 100) / 100,
    mortgageInsuranceMonthly: Math.round(mortgageInsuranceMonthly * 100) / 100,
    hoaMonthly: Math.round(hoaMonthly * 100) / 100,
    amortizationSchedule: schedule,
    yearlySummaries,
    explainabilityTrace: trace,
    plainLanguageExplanation: `For a ${loanPrincipal.toLocaleString()} ${currency} ${input.productType.toLowerCase().replace('_', ' ')} over ${amortizationYears} years at ${input.interestRate}% (${compounding.toLowerCase()} compounding), your regular ${frequency} payment is ${periodicPayment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}. Over the entire loan, you will pay ${totalInterestPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency} in total interest.`,
    rulesetVersion: pack.rulesetVersion,
    engineVersion: '2026.1-YAMOOPAY-LEND-CORE',
    confidence: 'HIGH',
    officialSources: pack.officialSources,
    disclaimer: pack.disclosures[0] || 'YamooPay provides calculations for informational purposes only. Terms depend on contract with lender.',
  };
}

/**
 * Affordability / Home Budget Solver
 */
export function calculateAffordability(input: {
  grossAnnualIncome: number;
  monthlyDebtPayments: number;
  downPaymentSaved: number;
  interestRate: number;
  termYears: number;
  countryCode?: string;
  frontEndDtiPercent?: number; // default 28%
  backEndDtiPercent?: number; // default 36%
  propertyTaxRate?: number;
  homeInsuranceRate?: number;
}): AffordabilityCalculationResult {
  const grossMonthlyIncome = input.grossAnnualIncome / 12;
  const frontEndDti = (input.frontEndDtiPercent || 28) / 100;
  const backEndDti = (input.backEndDtiPercent || 36) / 100;

  // Maximum allowed monthly housing payment (PITI) under front-end rule
  const maxHousingByFrontEnd = grossMonthlyIncome * frontEndDti;

  // Maximum allowed monthly housing payment under back-end rule
  const maxHousingByBackEnd = Math.max(0, grossMonthlyIncome * backEndDti - input.monthlyDebtPayments);

  const allowableMonthlyPITI = Math.min(maxHousingByFrontEnd, maxHousingByBackEnd);
  const limitingFactor =
    maxHousingByFrontEnd <= maxHousingByBackEnd
      ? 'FRONT_END_HOUSING_RATIO'
      : 'BACK_END_TOTAL_DEBT_RATIO';

  // Reserve ~18% of monthly budget for property tax & insurance
  const estimatedTaxAndInsuranceFraction = 0.18;
  const maxMonthlyPrincipalAndInterest = allowableMonthlyPITI * (1 - estimatedTaxAndInsuranceFraction);
  const estimatedPropertyTaxMonthly = allowableMonthlyPITI * 0.12;
  const estimatedInsuranceMonthly = allowableMonthlyPITI * 0.06;

  // Reverse solve maximum loan amount from monthly P&I
  const monthlyRate = (input.interestRate / 100) / 12;
  const totalMonths = input.termYears * 12;

  let maxLoanAmount = 0;
  if (monthlyRate === 0) {
    maxLoanAmount = maxMonthlyPrincipalAndInterest * totalMonths;
  } else {
    const factor = Math.pow(1 + monthlyRate, totalMonths);
    maxLoanAmount = maxMonthlyPrincipalAndInterest * ((factor - 1) / (monthlyRate * factor));
  }

  const maxHomePrice = maxLoanAmount + input.downPaymentSaved;
  const conservativePrice = maxHomePrice * 0.88;
  const aggressivePrice = maxHomePrice * 1.05;

  return {
    maxAffordableHomePrice: Math.round(maxHomePrice),
    maxAffordableLoanAmount: Math.round(maxLoanAmount),
    suggestedDownPayment: input.downPaymentSaved,
    monthlyBudgetPITI: Math.round(allowableMonthlyPITI),
    maxMonthlyPrincipalAndInterest: Math.round(maxMonthlyPrincipalAndInterest),
    estimatedPropertyTaxMonthly: Math.round(estimatedPropertyTaxMonthly),
    estimatedInsuranceMonthly: Math.round(estimatedInsuranceMonthly),
    frontEndDtiActual: Math.round((allowableMonthlyPITI / grossMonthlyIncome) * 1000) / 10,
    backEndDtiActual:
      Math.round(((allowableMonthlyPITI + input.monthlyDebtPayments) / grossMonthlyIncome) * 1000) / 10,
    priceRangeConservative: Math.round(conservativePrice),
    priceRangeAggressive: Math.round(aggressivePrice),
    limitingFactor,
    assumptions: [
      `Assumes a ${((input.frontEndDtiPercent || 28))}% Front-End housing ratio limit and ${((input.backEndDtiPercent || 36))}% Back-End total debt ratio limit.`,
      `Includes estimated escrow for property tax (~1.1%/yr) and homeowners insurance (~0.4%/yr).`,
      `Calculated based on a ${input.termYears}-year fixed rate mortgage at ${input.interestRate}%.`,
      `This estimate is for budgeting and planning only; lenders apply personalized underwriting guidelines.`,
    ],
  };
}

/**
 * Mortgage Refinance Analyzer
 */
export function calculateRefinance(input: {
  currentBalance: number;
  currentRate: number;
  currentRemainingYears: number;
  currentMonthlyPayment: number;
  newRate: number;
  newTermYears: number;
  refinanceClosingCosts: number;
}): RefinanceCalculationResult {
  const currentMonths = input.currentRemainingYears * 12;
  const currentMonthlyRate = (input.currentRate / 100) / 12;

  // Remaining interest on current mortgage
  let totalRemainingCurrentInterest = 0;
  if (input.currentMonthlyPayment > 0) {
    totalRemainingCurrentInterest = Math.max(0, input.currentMonthlyPayment * currentMonths - input.currentBalance);
  } else {
    const calcCurrentPmt = calculateStandardPayment(input.currentBalance, currentMonthlyRate, currentMonths);
    totalRemainingCurrentInterest = calcCurrentPmt * currentMonths - input.currentBalance;
  }

  // New mortgage payment
  const newMonths = input.newTermYears * 12;
  const newMonthlyRate = (input.newRate / 100) / 12;
  const newMonthlyPayment = calculateStandardPayment(input.currentBalance, newMonthlyRate, newMonths);
  const totalNewInterest = newMonthlyPayment * newMonths - input.currentBalance;

  const currentPmt = input.currentMonthlyPayment || calculateStandardPayment(input.currentBalance, currentMonthlyRate, currentMonths);
  const monthlySavings = currentPmt - newMonthlyPayment;
  const lifetimeInterestSavings = totalRemainingCurrentInterest - totalNewInterest;
  const netLifetimeSavings = lifetimeInterestSavings - input.refinanceClosingCosts;

  let breakEvenMonths = 0;
  if (monthlySavings > 0) {
    breakEvenMonths = Math.ceil(input.refinanceClosingCosts / monthlySavings);
  }

  const now = new Date();
  const payoffCurrent = new Date(now.getTime());
  payoffCurrent.setMonth(payoffCurrent.getMonth() + currentMonths);

  const payoffNew = new Date(now.getTime());
  payoffNew.setMonth(payoffNew.getMonth() + newMonths);

  const isBeneficial = netLifetimeSavings > 0 && monthlySavings > 0;
  let recommendationSummary = '';
  if (isBeneficial) {
    recommendationSummary = `Refinancing lowers your monthly payment by ${monthlySavings.toFixed(2)} and recovers the ${input.refinanceClosingCosts.toLocaleString()} closing costs in ${breakEvenMonths} months, yielding ${netLifetimeSavings.toLocaleString()} in net lifetime savings.`;
  } else if (monthlySavings > 0 && netLifetimeSavings <= 0) {
    recommendationSummary = `Refinancing reduces your monthly payment by ${monthlySavings.toFixed(2)}, but extending the loan to ${input.newTermYears} years increases total lifetime interest.`;
  } else {
    recommendationSummary = `Refinancing at ${input.newRate}% with ${input.newTermYears} years does not yield monthly or lifetime savings vs your current loan terms.`;
  }

  return {
    currentMonthlyPayment: Math.round(currentPmt * 100) / 100,
    newMonthlyPayment: Math.round(newMonthlyPayment * 100) / 100,
    monthlySavings: Math.round(monthlySavings * 100) / 100,
    totalRemainingCurrentInterest: Math.round(totalRemainingCurrentInterest * 100) / 100,
    totalNewInterest: Math.round(totalNewInterest * 100) / 100,
    lifetimeInterestSavings: Math.round(lifetimeInterestSavings * 100) / 100,
    refinanceClosingCosts: input.refinanceClosingCosts,
    netLifetimeSavings: Math.round(netLifetimeSavings * 100) / 100,
    breakEvenMonths,
    payoffDateCurrent: payoffCurrent.toISOString().slice(0, 10),
    payoffDateNew: payoffNew.toISOString().slice(0, 10),
    isBeneficial,
    recommendationSummary,
  };
}

/**
 * Executes Golden Test Cases and returns results
 */
export function runGoldenLoanTests(): {
  total: number;
  passed: number;
  failed: number;
  results: {
    id: string;
    scenarioName: string;
    expectedPayment: number;
    actualPayment: number;
    diffPayment: number;
    status: 'PASS' | 'FAIL';
  }[];
} {
  const results = GOLDEN_LOAN_TEST_CASES.map((tc) => {
    const calcResult = calculateLoan({
      mode: 'loan-calculator',
      productType: tc.productType,
      countryCode: tc.countryCode,
      currency: 'USD',
      loanAmount: tc.loanAmount,
      interestRate: tc.interestRate,
      rateType: 'FIXED',
      termYears: tc.termYears,
      paymentFrequency: tc.frequency,
      compoundingConvention: tc.compounding,
    });

    const diffPayment = Math.abs(calcResult.periodicPayment - tc.expectedPayment);
    const isPass = diffPayment <= tc.tolerance;

    return {
      id: tc.id,
      scenarioName: tc.scenarioName,
      expectedPayment: tc.expectedPayment,
      actualPayment: calcResult.periodicPayment,
      diffPayment: Math.round(diffPayment * 100) / 100,
      status: (isPass ? 'PASS' : 'FAIL') as 'PASS' | 'FAIL',
    };
  });

  const passed = results.filter((r) => r.status === 'PASS').length;
  return {
    total: results.length,
    passed,
    failed: results.length - passed,
    results,
  };
}
